import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { duasAPI, categoriesAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';

const DuaList = () => {
  const [duas, setDuas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('');
  const [confirmModal, setConfirmModal] = useState({ show: false, duaId: null, action: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchDuas();
  }, [currentPage, status, category]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getDuaCategories();
      if (response.data.success) {
        setCategories(response.data.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchDuas = async () => {
    setLoading(true);
    try {
      const response = await duasAPI.getAll({
        status,
        category,
        page: currentPage,
        limit: 20,
      });

      if (response.data.success) {
        setDuas(response.data.data.duas);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch duas:', error);
      setToast({ show: true, message: 'Failed to fetch duas', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (duaId) => {
    try {
      await duasAPI.delete(duaId);
      setToast({ show: true, message: 'Dua deleted successfully', type: 'success' });
      fetchDuas();
    } catch (error) {
      setToast({ show: true, message: 'Failed to delete dua', type: 'error' });
    }
    setConfirmModal({ show: false, duaId: null, action: null });
  };

  const handleRestore = async (duaId) => {
    try {
      await duasAPI.restore(duaId);
      setToast({ show: true, message: 'Dua restored successfully', type: 'success' });
      fetchDuas();
    } catch (error) {
      setToast({ show: true, message: 'Failed to restore dua', type: 'error' });
    }
    setConfirmModal({ show: false, duaId: null, action: null });
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, duaId: null, action: null })}
        onConfirm={() => confirmModal.action === 'delete' ? handleDelete(confirmModal.duaId) : handleRestore(confirmModal.duaId)}
        title={confirmModal.action === 'delete' ? 'Delete Dua' : 'Restore Dua'}
        message={confirmModal.action === 'delete' ? 'Are you sure you want to delete this dua?' : 'Are you sure you want to restore this dua?'}
        type={confirmModal.action === 'delete' ? 'danger' : 'warning'}
      />

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Duas Management</h2>
            <p className="page-subtitle">Manage Islamic supplications</p>
          </div>
          <Link to="/duas/new" className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>New Dua
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="stat-card mb-4">
        <div className="row g-3">
          <div className="col-md-5">
            <label className="form-label">Status</label>
            <select className="form-select" value={status} onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}>
              <option value="all">All Duas</option>
              <option value="published">Published</option>
              <option value="unpublished">Unpublished</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Category</label>
            <select className="form-select" value={category} onChange={(e) => { setCategory(e.target.value); setCurrentPage(1); }}>
              <option value="">All Categories</option>
              {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
            </select>
          </div>
          <div className="col-md-1">
            <label className="form-label">&nbsp;</label>
            <button className="btn btn-outline-primary w-100" onClick={fetchDuas}>
              <i className="bi bi-arrow-clockwise me-2"></i>Refresh
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Reference</th>
                  <th>Display Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {duas.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No duas found</p>
                    </td>
                  </tr>
                ) : (
                  duas.map((dua) => (
                    <tr key={dua.id}>
                      <td>
                        <div className="fw-semibold">{dua.title}</div>
                        <small className="text-muted">{dua.arabic?.substring(0, 40)}...</small>
                      </td>
                      <td><span className="badge badge-purple">{categories.find(c => c.id === dua.category_id)?.name}</span></td>
                      <td><small className="text-muted">{dua.reference || 'N/A'}</small></td>
                      <td><small>{dua.display_order}</small></td>
                      <td>
                        {dua.deleted_at ? (
                          <span className="badge badge-danger">Deleted</span>
                        ) : dua.is_published ? (
                          <span className="badge badge-success">Published</span>
                        ) : (
                          <span className="badge badge-warning">Draft</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          {!dua.deleted_at && (
                            <Link to={`/duas/edit/${dua.id}`} className="btn btn-sm btn-outline-primary" title="Edit">
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}
                          {dua.deleted_at ? (
                            <button className="btn btn-sm btn-outline-success" onClick={() => setConfirmModal({ show: true, duaId: dua.id, action: 'restore' })} title="Restore">
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                          ) : (
                            <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmModal({ show: true, duaId: dua.id, action: 'delete' })} title="Delete">
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DuaList;